import axios, { AxiosRequestConfig } from 'axios'

import { MAX_PROGRESS_BEFORE_RESPONSE, MIN_DURATION_MS } from '../constants'
import { UploadItem } from '../types'
import { apiPrefix } from '../utils/apiUtils'
import { generateUploadId, getFileType } from '../utils/fileUtils'

export class UploadProgressStore {
  private uploads: Map<string, UploadItem> = new Map()
  private listeners: Set<() => void> = new Set()
  private targetEndpoint: string | null = null
  private formDataParams: Map<string, any> = new Map()
  private fileKey = 'file'
  private progressTimers: Map<string, NodeJS.Timeout> = new Map()
  private shrinkTimers: Map<string, NodeJS.Timeout> = new Map()
  private displayProgressTimers: Map<string, NodeJS.Timeout> = new Map()
  private onSuccessCallback: ((uploadItem: UploadItem) => void) | null = null
  private version = 0
  private notifyDebounceTimer: NodeJS.Timeout | null = null
  private readonly NOTIFY_DEBOUNCE_MS = 100

  getUploads(): Map<string, UploadItem> {
    return this.uploads
  }

  registerUpload(upload: UploadItem): void {
    if (!this.targetEndpoint && upload.config.url) {
      this.targetEndpoint = upload.config.url ? apiPrefix(upload.config.url) : null
    }
    const newUpload: UploadItem = {
      ...upload,
      displayProgress: upload.displayProgress || 0
    }
    this.uploads.set(upload.id, newUpload)
    this.notifyListeners()
  }

  setFormDataParams(params: Map<string, any>, fileKey?: string): void {
    this.formDataParams = params
    if (fileKey) {
      this.fileKey = fileKey
    }
  }

  getTargetEndpoint(): string | null {
    return this.targetEndpoint
  }

  getFormDataParams(): Map<string, any> {
    return this.formDataParams
  }

  updateUploadProgress(id: string, progress: number): void {
    const upload = this.uploads.get(id)
    if (!upload) {
      return
    }

    const clampedProgress = Math.max(0, Math.min(100, progress))
    const elapsed = Date.now() - upload.createdAt.getTime()
    const minDurationElapsed = elapsed >= MIN_DURATION_MS

    if (clampedProgress >= 100) {
      const maxProgress = MAX_PROGRESS_BEFORE_RESPONSE

      if (!upload.dataUploaded) {
        const updatedUpload: UploadItem = {
          ...upload,
          progress: 100,
          displayProgress: Math.min(maxProgress, upload.displayProgress || maxProgress),
          dataUploaded: true
        }
        this.uploads.set(id, updatedUpload)
        this.notifyListeners()
      }
    } else {
      if (upload.displayProgress !== clampedProgress || upload.progress !== clampedProgress) {
        let displayProgress = clampedProgress
        const maxProgress = upload.dataUploaded ? 100 : MAX_PROGRESS_BEFORE_RESPONSE

        if (!minDurationElapsed && clampedProgress > 0) {
          const minProgress = Math.floor((elapsed / MIN_DURATION_MS) * 100)
          displayProgress = Math.max(clampedProgress, Math.min(minProgress, maxProgress - 1))
        } else {
          displayProgress = Math.min(clampedProgress, maxProgress)
        }

        const monotonicDisplayProgress = Math.max(upload.displayProgress || 0, displayProgress)
        const updatedUpload: UploadItem = {
          ...upload,
          progress: clampedProgress,
          displayProgress: monotonicDisplayProgress
        }
        this.uploads.set(id, updatedUpload)
        this.debouncedNotifyListeners()
      }
    }
  }

  completeUpload(id: string, uploadedTo?: string): void {
    const upload = this.uploads.get(id)
    if (!upload) {
      return
    }

    const elapsed = Date.now() - upload.createdAt.getTime()
    const remainingTime = Math.max(0, MIN_DURATION_MS - elapsed)
    const startProgress = upload.displayProgress || MAX_PROGRESS_BEFORE_RESPONSE

    if (this.displayProgressTimers.has(id)) {
      clearInterval(this.displayProgressTimers.get(id)!)
      this.displayProgressTimers.delete(id)
    }

    if (remainingTime > 0 && !this.progressTimers.has(id)) {
      const progressDiff = 100 - startProgress
      const steps = Math.max(20, Math.ceil(remainingTime / 50))
      const stepTime = remainingTime / steps
      let currentStep = 0

      const timer = setInterval(() => {
        currentStep++
        const progress = startProgress + (progressDiff * currentStep) / steps
        const displayProgress = Math.min(100, Math.round(progress))

        const currentUpload = this.uploads.get(id)
        if (!currentUpload) {
          clearInterval(timer)
          this.progressTimers.delete(id)
          return
        }

        const updatedUpload: UploadItem = {
          ...currentUpload,
          progress: 100,
          displayProgress
        }
        this.uploads.set(id, updatedUpload)

        if (currentStep % 3 === 0 || currentStep >= steps || displayProgress >= 100) {
          this.debouncedNotifyListeners()
        }

        if (currentStep >= steps || displayProgress >= 100) {
          const finalUpload: UploadItem = {
            ...currentUpload,
            progress: 100,
            displayProgress: 100,
            status: 'success',
            uploadedTo: uploadedTo || currentUpload.uploadedTo
          }
          this.uploads.set(id, finalUpload)
          this.notifyListeners()
          clearInterval(timer)
          this.progressTimers.delete(id)
          this.startShrinkAnimation(id)
          if (this.onSuccessCallback) {
            this.onSuccessCallback(finalUpload)
          }
        }
      }, stepTime)
      this.progressTimers.set(id, timer)
    } else {
      if (this.progressTimers.has(id)) {
        const timer = this.progressTimers.get(id)!
        clearInterval(timer)
        this.progressTimers.delete(id)
      }

      const updatedUpload: UploadItem = {
        ...upload,
        progress: 100,
        displayProgress: 100,
        status: 'success',
        uploadedTo: uploadedTo || upload.uploadedTo
      }
      this.uploads.set(id, updatedUpload)
      this.notifyListeners()
      this.startShrinkAnimation(id)
      if (this.onSuccessCallback) {
        this.onSuccessCallback(updatedUpload)
      }
    }
  }

  failUpload(id: string, error: string): void {
    const upload = this.uploads.get(id)
    if (upload) {
      const updatedUpload: UploadItem = {
        ...upload,
        status: 'error',
        error
      }
      this.uploads.set(id, updatedUpload)
      this.notifyListeners()
    }
  }

  cancelUpload(id: string): void {
    const upload = this.uploads.get(id)
    if (upload && upload.status === 'uploading') {
      if (this.progressTimers.has(id)) {
        clearInterval(this.progressTimers.get(id)!)
        this.progressTimers.delete(id)
      }
      if (this.displayProgressTimers.has(id)) {
        clearInterval(this.displayProgressTimers.get(id)!)
        this.displayProgressTimers.delete(id)
      }
      upload.cancelToken.cancel('Upload cancelled by user')
      const updatedUpload: UploadItem = {
        ...upload,
        status: 'cancelled'
      }
      this.uploads.set(id, updatedUpload)
      this.notifyListeners()
    }
  }

  async retryUpload(id: string): Promise<void> {
    const upload = this.uploads.get(id)
    if (!upload || (upload.status !== 'error' && upload.status !== 'cancelled')) {
      return
    }

    const newId = generateUploadId()
    const cancelToken = axios.CancelToken.source()

    const originalConfig = upload.config
    const url = originalConfig.url ? apiPrefix(originalConfig.url) : '/api'
    const newConfig: AxiosRequestConfig = {
      ...originalConfig,
      url,
      cancelToken: cancelToken.token,
      // @ts-expect-error: add custom metadata property to the axios config for internal tracking
      metadata: { ...originalConfig.metadata, uploadId: newId }
    }

    const progressHandler = (progressEvent: any) => {
      if (progressEvent.total && progressEvent.total > 0) {
        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
        this.updateUploadProgress(newId, Math.max(0, Math.min(100, progress)))
      } else if (progressEvent.loaded > 0) {
        this.updateUploadProgress(newId, 1)
      } else {
        this.updateUploadProgress(newId, 0)
      }
    }

    newConfig.onUploadProgress = progressHandler

    const newUpload: UploadItem = {
      id: newId,
      file: upload.file,
      fileName: upload.fileName,
      fileType: upload.fileType,
      status: 'uploading',
      progress: 0,
      displayProgress: 0,
      cancelToken,
      config: newConfig,
      createdAt: new Date()
    }

    this.uploads.delete(id)
    this.uploads.set(newId, newUpload)
    this.notifyListeners()

    try {
      await axios(newConfig)
    } catch (error: any) {
      if (!axios.isCancel(error)) {
        this.failUpload(newId, error.message || 'Upload failed')
      }
    }
  }

  removeUpload(id: string): void {
    if (this.progressTimers.has(id)) {
      clearInterval(this.progressTimers.get(id)!)
      this.progressTimers.delete(id)
    }
    if (this.displayProgressTimers.has(id)) {
      clearInterval(this.displayProgressTimers.get(id)!)
      this.displayProgressTimers.delete(id)
    }
    if (this.shrinkTimers.has(id)) {
      clearTimeout(this.shrinkTimers.get(id)!)
      this.shrinkTimers.delete(id)
    }
    this.uploads.delete(id)
    if (this.uploads.size === 0) {
      this.targetEndpoint = null
      this.formDataParams = new Map()
      this.fileKey = 'file'
    }
    this.notifyListeners()
  }

  startShrinkAnimation(id: string): void {
    const upload = this.uploads.get(id)
    if (!upload) {
      return
    }
    const updatedUpload: UploadItem = {
      ...upload,
      isShrinking: true
    }
    this.uploads.set(id, updatedUpload)
    this.notifyListeners()

    const timer = setTimeout(() => {
      const currentUpload = this.uploads.get(id)
      if (currentUpload) {
        const finalUpload: UploadItem = {
          ...currentUpload,
          isShrinking: false
        }
        this.uploads.set(id, finalUpload)
        this.notifyListeners()
      }
      this.shrinkTimers.delete(id)
    }, 500)
    this.shrinkTimers.set(id, timer)
  }

  async addFiles(files: File[]): Promise<void> {
    if (!this.targetEndpoint) {
      throw new Error('No target endpoint set. Upload at least one file first.')
    }

    for (const file of files) {
      const uploadId = generateUploadId()
      const cancelToken = axios.CancelToken.source()

      const formData = new FormData()
      this.formDataParams.forEach((value, key) => {
        formData.append(key, value)
      })

      formData.append(this.fileKey, file)

      const progressHandler = (progressEvent: any) => {
        if (progressEvent.total && progressEvent.total > 0) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
          this.updateUploadProgress(uploadId, Math.max(0, Math.min(100, progress)))
        } else if (progressEvent.loaded > 0) {
          this.updateUploadProgress(uploadId, 1)
        } else {
          this.updateUploadProgress(uploadId, 0)
        }
      }

      const config: AxiosRequestConfig = {
        url: apiPrefix(this.targetEndpoint),
        method: 'POST',
        data: formData,
        cancelToken: cancelToken.token,
        onUploadProgress: progressHandler,
        // @ts-expect-error: add custom metadata property to the axios config for internal tracking
        metadata: { uploadId }
      }

      const uploadItem: UploadItem = {
        id: uploadId,
        file,
        fileName: file.name,
        fileType: getFileType(file.name),
        status: 'uploading',
        progress: 0,
        displayProgress: 0,
        cancelToken,
        config,
        createdAt: new Date()
      }

      this.uploads.set(uploadId, uploadItem)
      this.notifyListeners()

      try {
        const response = await axios(config)
        const uploadedTo = response.data?.uploadedTo || response.data?.folder || response.data?.path
        this.completeUpload(uploadId, uploadedTo)
      } catch (error: any) {
        if (!axios.isCancel(error)) {
          const errorMessage = error.response?.data?.message || error.message || 'Upload failed'
          this.failUpload(uploadId, errorMessage)
        }
      }
    }
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  getVersion(): number {
    return this.version
  }

  setOnSuccess(callback: (uploadItem: UploadItem) => void): void {
    this.onSuccessCallback = callback
  }

  clearAllUploads(): void {
    this.progressTimers.forEach((timer) => clearInterval(timer))
    this.progressTimers.clear()
    this.displayProgressTimers.forEach((timer) => clearInterval(timer))
    this.displayProgressTimers.clear()
    this.shrinkTimers.forEach((timer) => clearTimeout(timer))
    this.shrinkTimers.clear()
    this.uploads.clear()
    this.targetEndpoint = null
    this.formDataParams = new Map()
    this.fileKey = 'file'
    this.notifyListeners()
  }

  private debouncedNotifyListeners(): void {
    if (this.notifyDebounceTimer) {
      clearTimeout(this.notifyDebounceTimer)
    }

    this.notifyDebounceTimer = setTimeout(() => {
      this.notifyListeners()
      this.notifyDebounceTimer = null
    }, this.NOTIFY_DEBOUNCE_MS)
  }

  private notifyListeners(): void {
    if (this.notifyDebounceTimer) {
      clearTimeout(this.notifyDebounceTimer)
      this.notifyDebounceTimer = null
    }
    this.version++
    this.listeners.forEach((listener) => listener())
  }
}

export const uploadProgressStore = new UploadProgressStore()
